import type { FloatingAlign, FloatingSide } from '../types/floating.ts';
import { Popover as BasePopover } from '@base-ui/react/popover';
import clsx from 'clsx';

export interface PopoverProps extends React.PropsWithChildren {
  defaultOpen?: boolean;
  focusMode?: 'none' | 'trap' | 'modal';
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
}

export function Popover({
  children,
  defaultOpen,
  focusMode = 'none',
  onOpenChange,
  open
}: PopoverProps) {
  const modal = focusMode === 'modal' ? true : focusMode === 'trap' ? 'trap-focus' : false;

  return (
    <BasePopover.Root
      defaultOpen={defaultOpen}
      modal={modal}
      onOpenChange={nextOpen => onOpenChange?.(nextOpen)}
      open={open}
    >
      {children}
    </BasePopover.Root>
  );
}

export interface PopoverTriggerState {
  disabled: boolean;
  open: boolean;
}

export interface PopoverTriggerProps extends Omit<
  React.ComponentPropsWithRef<'button'>,
  'className'
> {
  className?: string | ((state: PopoverTriggerState) => string | undefined);
  closeDelay?: number;
  delay?: number;
  openOnHover?: boolean;
  render?: React.ReactElement;
}

export function PopoverTrigger({ className, ...props }: PopoverTriggerProps) {
  return (
    <BasePopover.Trigger
      {...props}
      className={state =>
        typeof className === 'function'
          ? className({ disabled: state.disabled, open: state.open })
          : className
      }
    />
  );
}

export interface PopoverPopupProps
  extends Omit<React.ComponentPropsWithRef<'div'>, 'children'>, React.PropsWithChildren {
  align?: FloatingAlign;
  alignOffset?: number;
  collisionPadding?: number;
  finalFocus?: boolean | React.RefObject<HTMLElement | null>;
  height?: 'available' | 'natural';
  initialFocus?: boolean | React.RefObject<HTMLElement | null>;
  positionMethod?: 'absolute' | 'fixed';
  side?: FloatingSide;
  sideOffset?: number;
}

export function PopoverPopup({
  align = 'center',
  alignOffset = 0,
  children,
  className,
  collisionPadding = 5,
  finalFocus,
  height = 'available',
  initialFocus,
  positionMethod = 'absolute',
  side = 'bottom',
  sideOffset = 0,
  ...props
}: PopoverPopupProps) {
  return (
    <BasePopover.Portal>
      <BasePopover.Positioner
        align={align}
        alignOffset={alignOffset}
        className="z-50 outline-none"
        collisionAvoidance={
          height === 'available'
            ? { align: 'shift', fallbackAxisSide: 'none', side: 'flip' }
            : undefined
        }
        collisionPadding={collisionPadding}
        positionMethod={positionMethod}
        side={side}
        sideOffset={sideOffset}
      >
        <BasePopover.Popup
          {...props}
          className={clsx(
            'origin-(--transform-origin) transition-[opacity,scale] duration-100 ease-out data-ending-style:scale-95 data-ending-style:opacity-0 data-starting-style:scale-95 data-starting-style:opacity-0 motion-reduce:transition-none',
            height === 'available' && 'max-h-(--available-height)',
            className
          )}
          finalFocus={finalFocus}
          initialFocus={initialFocus}
        >
          {children}
        </BasePopover.Popup>
      </BasePopover.Positioner>
    </BasePopover.Portal>
  );
}

export interface PopoverArrowProps extends React.ComponentPropsWithRef<'div'> {}

export function PopoverArrow({ className, ...props }: PopoverArrowProps) {
  return (
    <BasePopover.Arrow
      {...props}
      className={clsx(
        'size-2 rotate-45 data-[side=bottom]:-top-1 data-[side=left]:-right-1 data-[side=right]:-left-1 data-[side=top]:-bottom-1',
        className
      )}
    />
  );
}

export interface PopoverCloseProps extends React.ComponentPropsWithRef<'button'> {
  render?: React.ReactElement;
}

export function PopoverClose(props: PopoverCloseProps) {
  return <BasePopover.Close {...props} />;
}

export interface PopoverTitleProps extends React.ComponentPropsWithRef<'h2'> {}

export function PopoverTitle(props: PopoverTitleProps) {
  return <BasePopover.Title {...props} />;
}

export interface PopoverDescriptionProps extends React.ComponentPropsWithRef<'p'> {}

export function PopoverDescription(props: PopoverDescriptionProps) {
  return <BasePopover.Description {...props} />;
}
